import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { GoogleMap, LoadScript, MarkerClusterer, Marker, InfoWindow, OverlayView } from '@react-google-maps/api';

import { MapService, getAuthToken } from '../../service';
import { Lookups } from '../../utils';

import IconPort from './../../img/port.png';
import IconState from '../../img/state.png';
import IconCountry from '../../img/country.png';
import IconRegion from '../../img/region.png';
import IconIncentive from '../../img/incentive.png';
import IconFuel from '../../img/alt-fuel.png';
import MapPinInfo from '../map-pin-info/map-pin-info.component';

const containerStyle = {
    width: '100',
    height: '78vh'
};

const markerStyles = [
    {
        url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png',
        height: 53,
        width: 53
    },
    {
        url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png',
        height: 53,
        width: 53
    },
    {
        url: '/src/images/m3.png',
        height: 53,
        width: 53
    },
    {
        url: '/src/images/m4.png',
        height: 53,
        width: 53
    },
    {
        url: '/src/images/m5.png',
        height: 53,
        width: 53
    }
];

const stylesArray = [
    {
        elementType: 'geometry',
        stylers: [
            {
                color: '#f5f5f5'
            }
        ]
    },
    {
        elementType: 'labels.icon',
        stylers: [
            {
                visibility: 'off'
            }
        ]
    },
    {
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#333333'
            }
        ]
    },
    {
        elementType: 'labels.text.stroke',
        stylers: [
            {
                color: '#f5f5f5'
            }
        ]
    },
    {
        featureType: 'administrative.land_parcel',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#bdbdbd'
            }
        ]
    },
    {
        featureType: 'poi',
        elementType: 'geometry',
        stylers: [
            {
                color: '#eeeeee'
            }
        ]
    },
    {
        featureType: 'poi',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#757575'
            }
        ]
    },
    {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [
            {
                color: '#e5e5e5'
            }
        ]
    },
    {
        featureType: 'poi.park',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#9e9e9e'
            }
        ]
    },
    {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [
            {
                color: '#ffffff'
            }
        ]
    },
    {
        featureType: 'road.arterial',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#757575'
            }
        ]
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [
            {
                color: '#dadada'
            }
        ]
    },
    {
        featureType: 'road.highway',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#333333'
            }
        ]
    },
    {
        featureType: 'road.local',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#9e9e9e'
            }
        ]
    },
    {
        featureType: 'transit.line',
        elementType: 'geometry',
        stylers: [
            {
                color: '#e5e5e5'
            }
        ]
    },
    {
        featureType: 'transit.station',
        elementType: 'geometry',
        stylers: [
            {
                color: '#eeeeee'
            }
        ]
    },
    {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [
            {
                // visibility: 'simplified',
                color: '#c9c9c9'
            }
        ]
    },
    {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#9e9e9e'
            }
        ]
    }
];

const RenderLocationPins = ({ locationOption, locationPins, icon, onLocationPinClick, onLocationPinHover }) => {
    return (
        <>
            <MarkerClusterer gridSize={25} styles={markerStyles}>
                {(clusterer) =>
                    locationPins.map((locationPin, index) => (
                        <Marker
                            key={`${locationOption}~${locationPin._id}~${index}`}
                            position={locationPin}
                            clusterer={clusterer}
                            icon={icon}
                            onClick={(event) => {
                                onLocationPinClick(event, locationPin, locationOption);
                            }}
                            onMouseOver={(event) => {
                                onLocationPinHover(event, locationPin, locationOption);
                            }}
                            onMouseOut={(event) => {
                                onLocationPinHover(event, undefined, undefined);
                            }}
                        />
                    ))
                }
            </MarkerClusterer>
        </>
    );
};

const MapGoogle = ({ OnSeeMore, OnPinSelect, mapLayer, mapOptions, regionData, countryData, stateData, portData, portIncentivesData, countryFuelsData, selectedPin, selectedPinOption }) => {
    const mapInstance = useRef();

    const { instance, accounts } = useMsal();
    const [isLoading, setIsLoading] = useState(true);
    const [apiKey, setApiKey] = useState();

    const [mapCenter, setMapCenter] = useState({ lat: 25, lng: 0 });
    const [mapZoom, setMapZoom] = useState(2.6);
    const [hoveredPin, setHoveredPin] = useState(null);
    const [hoveredPinOption, setHoveredPinOption] = useState();

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            await getApiKey();
            setIsLoading(false);
        })();
    }, []);

    useEffect(() => {
        resetMap();
    }, [mapOptions]);

    useEffect(() => {
        if (!selectedPin) return;

        setMapCenter({ lat: selectedPin.lat + 15, lng: selectedPin.lng });
    }, [selectedPin]);

    const resetMap = () => {
        setMapZoom(2.6);
    };

    const getApiKey = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const data = await MapService.getMapKey(authToken);

        if (!data) {
            console.error('Map Key Error.');
            return;
        }

        setApiKey(data);
    };

    const getIcon = (option) => {
        const markerIcon = {
            url: '',
            scaledSize: {
                width: 20,
                height: 20
            }
        };

        if (mapLayer === Lookups.MapLayer.Incentive) markerIcon.url = IconIncentive;
        else if (mapLayer === Lookups.MapLayer.Fuels) {
            markerIcon.url = IconFuel;
        } else {
            if (option === Lookups.MapRegulatoryGroup.Region) markerIcon.url = IconRegion;
            else if (option === Lookups.MapRegulatoryGroup.Country) markerIcon.url = IconCountry;
            else if (option === Lookups.MapRegulatoryGroup.State) markerIcon.url = IconState;
            else if (option === Lookups.MapRegulatoryGroup.Port) markerIcon.url = IconPort;
        }

        return markerIcon;
    };

    const handleLocationPinSelect = (event, locationPin, locationOption) => {
        // Zoom based on location option (Region, Country, State, and Port)
        if (locationOption === Lookups.MapRegulatoryGroup.Region) setMapZoom(3);
        if (locationOption === Lookups.MapRegulatoryGroup.Country) setMapZoom(4);
        if (locationOption === Lookups.MapRegulatoryGroup.State) setMapZoom(6);
        if (locationOption === Lookups.MapRegulatoryGroup.Port) setMapZoom(11);

        // // Move location to the center of the map
        setMapCenter({ lat: locationPin.lat, lng: locationPin.lng });

        // Set locationPin and locaitonPinOption to show tool tip window
        OnPinSelect(locationPin, locationOption);
    };

    const handleLocationPinHover = (event, locationPin, locationOption) => {
        if (locationPin && locationOption === Lookups.MapRegulatoryGroup.Port && selectedPin !== locationPin) {
            setHoveredPin(locationPin);
            setHoveredPinOption(locationOption);
            return;
        }

        setHoveredPin();
        setHoveredPinOption();
    };

    const handleSeeMore = () => {
        OnSeeMore();
    };

    return (
        <>
            {!isLoading && apiKey && (
                <LoadScript googleMapsApiKey={apiKey}>
                    <GoogleMap
                        ref={(map) => {
                            mapInstance.current = map;
                        }}
                        mapContainerStyle={containerStyle}
                        center={mapCenter}
                        zoom={mapZoom}
                        options={{
                            mapTypeControl: false,
                            fullscreenControl: false,
                            streetViewControl: false,
                            styles: stylesArray,
                            minZoom: 2.6,
                            maxZoom: 20,
                            restriction: {
                                latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
                                strictBounds: true
                            }
                        }}
                    >
                        {/* Region */}
                        <RenderLocationPins
                            onLocationPinClick={handleLocationPinSelect}
                            onLocationPinHover={handleLocationPinHover}
                            locationOption={Lookups.MapRegulatoryGroup.Region}
                            locationPins={mapOptions.includes(Lookups.MapRegulatoryGroup.Region) ? regionData : []}
                            icon={getIcon(Lookups.MapRegulatoryGroup.Region)}
                        />

                        {/* Country */}
                        <RenderLocationPins
                            onLocationPinClick={handleLocationPinSelect}
                            onLocationPinHover={handleLocationPinHover}
                            locationOption={Lookups.MapRegulatoryGroup.Country}
                            locationPins={mapOptions.includes(Lookups.MapRegulatoryGroup.Country) ? countryData : []}
                            icon={getIcon(Lookups.MapRegulatoryGroup.Country)}
                        />

                        {/* State */}
                        <RenderLocationPins
                            onLocationPinClick={handleLocationPinSelect}
                            onLocationPinHover={handleLocationPinHover}
                            locationOption={Lookups.MapRegulatoryGroup.State}
                            locationPins={mapOptions.includes(Lookups.MapRegulatoryGroup.State) ? stateData : []}
                            icon={getIcon(Lookups.MapRegulatoryGroup.State)}
                        />

                        {/* Port */}
                        <RenderLocationPins
                            onLocationPinClick={handleLocationPinSelect}
                            onLocationPinHover={handleLocationPinHover}
                            locationOption={Lookups.MapRegulatoryGroup.Port}
                            locationPins={mapOptions.includes(Lookups.MapRegulatoryGroup.Port) ? portData : []}
                            icon={getIcon(Lookups.MapRegulatoryGroup.Port)}
                        />

                        {/* Port Incentive */}
                        <RenderLocationPins
                            onLocationPinClick={handleLocationPinSelect}
                            onLocationPinHover={handleLocationPinHover}
                            locationOption={Lookups.MapRegulatoryGroup.Port}
                            locationPins={mapLayer === Lookups.MapLayer.Incentive ? portIncentivesData : []}
                            icon={getIcon(Lookups.MapAreaGroup.Incentive)}
                        />

                        {/* Country Fuels */}
                        <RenderLocationPins
                            onLocationPinClick={handleLocationPinSelect}
                            onLocationPinHover={handleLocationPinHover}
                            locationOption={Lookups.MapRegulatoryGroup.Country}
                            locationPins={mapLayer === Lookups.MapLayer.Fuels && mapOptions.includes(Lookups.MapRegulatoryGroup.Country) ? countryFuelsData : []}
                            icon={getIcon(Lookups.MapRegulatoryGroup.Country)}
                        />

                        {/* Hovered Port Name Display */}
                        {hoveredPin && hoveredPinOption && (
                            <OverlayView
                                position={{
                                    lat: hoveredPin.lat,
                                    lng: hoveredPin.lng
                                }}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                            >
                                <div style={{ position: 'relative', backgroundColor: '#002A4D', borderRadius: '10px', transform: 'translate(-50%, -180%)' }}>
                                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'white', padding: '8px' }}>{hoveredPin.name}</div>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: '-9px',
                                            left: '50%',
                                            marginLeft: '-10px',
                                            width: '0',
                                            height: '0',
                                            borderLeft: '10px solid transparent',
                                            borderRight: '10px solid transparent',
                                            borderTop: '10px solid #002A4D'
                                        }}
                                    ></div>
                                </div>
                            </OverlayView>
                        )}

                        {selectedPin && selectedPinOption && (
                            <InfoWindow
                                onCloseClick={() => {
                                    OnPinSelect(undefined, undefined);
                                }}
                                position={{
                                    lat: selectedPin.lat,
                                    lng: selectedPin.lng
                                }}
                                options={{
                                    pixelOffset: { width: 0, height: -20 }
                                }}
                            >
                                <MapPinInfo OnSeeMore={handleSeeMore} locationPin={selectedPin} locationPinOption={selectedPinOption} style={{ margin: 0 }} />
                            </InfoWindow>
                        )}
                    </GoogleMap>
                </LoadScript>
            )}
        </>
    );
};

export default MapGoogle;
